import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  SnackbarContent,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../sdk";
import { LoadingButton } from "@mui/lab";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import ConfirmationDialog from "../../PrimarySetups/components/ConfirmationDialog";
import CloseIcon from "@mui/icons-material/Close";

const initialValue = {
  pushRatesTable: [],
};

const formatDateToYYYYMMDD = (dateStr) => {
  const year = dateStr?.year;
  const month = dateStr?.month?.toString().padStart(2, "0");
  const day = dateStr?.day?.toString().padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateToDDMMYYYY = (dateStr) => {
  const year = dateStr?.year;
  const month = dateStr?.month?.toString().padStart(2, "0");
  const day = dateStr?.day?.toString().padStart(2, "0");

  return `${day}-${month}-${year}`;
};

export const PushInnkeyRatesDialog = ({
  open,
  handleClose,
  pushRateFromDate,
  updatePushRate,
  currentHotel,
  isLoading,
}) => {
  const { hotelId, FILEID } = useParams();
  const { authFetch } = useAuth();
  const [showSnackbar, setShowSnackbar] = useState(null);
  const [
    showDisableOverrideConfirmationPopup,
    setShowDisableOverrideConfirmationPopup,
  ] = useState(null);

  const {
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    control,
    handleSubmit,
  } = useForm({
    defaultValues: initialValue,
    mode: "all",
  });

  const tableData = useWatch({ name: "pushRatesTable", control });

  const getTable = useCallback(async () => {
    const { get } = await authFetch({
      path: `/hotel/${hotelId}/get-push-price-details?date=${formatDateToYYYYMMDD(
        pushRateFromDate
      )}&recommendationId=${FILEID}`,
    });
    const { data, error, response } = await get();

    if (response?.ok) {
      const res = data?.[formatDateToYYYYMMDD(pushRateFromDate)];
      const tableData = res?.length ? res : [];
      setValue("pushRatesTable", tableData);
    } else {
      console.log(error);
    }
  }, [FILEID, authFetch, hotelId, pushRateFromDate, setValue]);

  const onSave = useCallback(
    (values) => {
      updatePushRate(values, pushRateFromDate);
    },
    [pushRateFromDate, updatePushRate]
  );

  useEffect(() => {
    getTable();
  }, [getTable]);

  // For showing values in the UI
  const getRateDisplay = useCallback(
    (value, index) => {
      const date = new Date(
        pushRateFromDate.year,
        pushRateFromDate.month - 1,
        pushRateFromDate.day
      );

      const tableRowData = tableData[index];
      const indexOfDay = date.getDay();

      let minThreshold, maxThreshold;

      if (indexOfDay && currentHotel?.midWeek?.includes(indexOfDay)) {
        minThreshold = tableRowData?.minThresholdWeekday;
        maxThreshold = tableRowData?.MaxThresholdWeekday;
      } else {
        minThreshold = tableRowData?.minThresholdWeekend;
        maxThreshold = tableRowData?.maxThresholdWeekend;
      }

      const numValue = Number(value);

      if (minThreshold && numValue < minThreshold) {
        return (
          <>
            {Math.round(minThreshold)}
            <RedAsterisk>*</RedAsterisk>
          </>
        );
      }

      if (maxThreshold && numValue > maxThreshold) {
        return (
          <>
            {maxThreshold}
            <RedAsterisk>*</RedAsterisk>
          </>
        );
      }

      return `${value}`;
    },
    [pushRateFromDate, tableData, currentHotel]
  );

  // For validate Rates
  const validateRate = useCallback(
    (value, index) => {
      const date = new Date(
        pushRateFromDate.year,
        pushRateFromDate.month - 1,
        pushRateFromDate.day
      );

      const tableRowData = tableData[index];
      const indexOfDay = date.getDay();

      let minThreshold, maxThreshold;

      if (indexOfDay && currentHotel?.midWeek?.includes(indexOfDay)) {
        minThreshold = tableRowData?.minThresholdWeekday;
        maxThreshold = tableRowData?.MaxThresholdWeekday;
      } else {
        minThreshold = tableRowData?.minThresholdWeekend;
        maxThreshold = tableRowData?.maxThresholdWeekend;
      }

      const numValue = Number(value);

      if (minThreshold && numValue < minThreshold) {
        return `Price should be more than ${Math.round(minThreshold)}`;
      }

      if (maxThreshold && numValue > maxThreshold) {
        return `Price should be less than ${maxThreshold}`;
      }

      return null;
    },
    [pushRateFromDate, tableData, currentHotel]
  );

  const handleChangePushRate = useCallback(
    (event, index) => {
      const { value } = event.target;
      const positiveNumberRegex = /^(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)?$/;

      if (positiveNumberRegex.test(value)) {
        const errorMessage = validateRate(value, index);

        if (errorMessage) {
          setError(`pushRatesTable.${index}.pushOverridePrice`, {
            type: "custom",
            message: errorMessage,
          });
        } else {
          clearErrors(`pushRatesTable.${index}.pushOverridePrice`);
        }

        setValue(`pushRatesTable.${index}.pushOverridePrice`, Number(value));
      }
    },
    [clearErrors, setError, setValue, validateRate]
  );

  const disablePushOverride = async (date, roomId, ratePlanId) => {
    try {
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/disable-push-override`,
      });
      const payload = {
        recommendationId: Number(FILEID),
        date: formatDateToYYYYMMDD(date),
        roomId: roomId,
        rateId: ratePlanId,
      };
      const { error, response } = await post(payload);
      if (response?.ok) {
        getTable();
        setShowSnackbar({
          isError: false,
          message: response?.data?.message,
        });
      } else {
        console.log(error);
        setShowSnackbar({
          isError: true,
          message: error?.messageToUser || "Something Went Wrong",
        });
      }
    } catch (error) {
      console.log(error);
      setShowSnackbar({ isError: true, message: "Something Went Wrong" });
    }
  };

  const handleChangeOverrideCheckbox = (event, rowData, index) => {
    if (event.target.checked) {
      setValue(`pushRatesTable.${index}.isPushOverride`, event.target.checked);
    } else {
      setShowDisableOverrideConfirmationPopup({
        date: pushRateFromDate,
        roomId: rowData?.roomId,
        rateId: rowData?.roomRateGroupCode,
      });
    }
  };

  const tableHeadings = [
    {
      key: "roomTypes",
      label: "Room Types",
      style: { minWidth: "150px" },
    },
    {
      key: "leftToSell",
      label: "Left to Sell",
    },
    {
      key: "recommendedRate",
      label: "Recommended Rate",
    },
    {
      key: "overrideRate",
      label: "Override Rate",
    },
    {
      key: "promotionalDiscount",
      label: "Promotional Discount",
    },
    {
      key: "pmsRates",
      label: "New Rates in PMS Rates",
    },
  ];

  return (
    <StyledChannelDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="content">
        <Typography variant="body1">
          Push Rates to PMS ({formatDateToDDMMYYYY(pushRateFromDate)})
        </Typography>
      </DialogTitle>

      <DialogContent className="content">
        <Box
          sx={{ maxHeight: "500px", overflow: "auto", mb: "8px" }}
          component="form"
        >
          <Table>
            <TableHead>
              <StyledTableRow>
                {tableHeadings.map((column) => (
                  <HeaderTableCell key={column?.key} sx={column?.style}>
                    {column?.label}
                  </HeaderTableCell>
                ))}
              </StyledTableRow>
            </TableHead>

            <TableBody>
              {tableData.map((rowData, index) => (
                <React.Fragment key={`${rowData} - ${index}`}>
                  <StyledTableRow>
                    <CustomTableCell>
                      {rowData?.roomName || "-"}
                    </CustomTableCell>
                    <CustomTableCell>
                      {rowData?.inventory || "-"}
                    </CustomTableCell>
                    <CustomTableCell>
                      {getRateDisplay(rowData?.recommendedPrice, index)}
                    </CustomTableCell>
                    <CustomTableCell
                      rowSpan={2}
                      style={{
                        backgroundColor: rowData?.isPushOverride
                          ? "#1976D229"
                          : "#0000000A",
                      }}
                    >
                      <Stack direction={"row"} alignItems={"center"}>
                        <Checkbox
                          size="small"
                          checked={rowData?.isPushOverride}
                          onChange={(event) =>
                            handleChangeOverrideCheckbox(event, rowData, index)
                          }
                        />
                        <StyledTextField
                          variant="standard"
                          disabled={!rowData?.isPushOverride}
                          value={
                            tableData?.[index]?.pushOverridePrice
                              ? Math.round(
                                  tableData?.[index]?.pushOverridePrice
                                )
                              : ""
                          }
                          sx={{
                            maxWidth: "60px",
                          }}
                          error={
                            !!errors?.pushRatesTable?.[index]?.pushOverridePrice
                          }
                          helperText={
                            errors?.pushRatesTable?.[index]?.pushOverridePrice
                              ? errors?.pushRatesTable?.[index]
                                  ?.pushOverridePrice?.message
                              : ""
                          }
                          inputProps={{
                            style: {
                              fontSize: "10px",
                              textAlign: "center",
                            },
                          }}
                          onChange={(e) => handleChangePushRate(e, index)}
                          type="number"
                        />
                      </Stack>
                    </CustomTableCell>
                    <CustomTableCell
                      style={{ backgroundColor: "#0000000A" }}
                      rowSpan={2}
                    >
                      {`${rowData?.discount} %`}
                    </CustomTableCell>
                    <CustomTableCell style={{ backgroundColor: "#1976D229" }}>
                      {Math.round(
                        rowData?.isPushOverride && rowData.pushOverridePrice
                          ? rowData.pushOverridePrice
                          : rowData.pushPrice
                      )}
                    </CustomTableCell>
                  </StyledTableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          type="submit"
          disabled={errors?.pushRatesTable || isLoading}
          onClick={handleSubmit(onSave)}
        >
          Submit
        </LoadingButton>
      </DialogContent>

      <CloseIcon className="closeButton" onClick={handleClose} />

      <ConfirmationDialog
        warningIcon
        handleCloseConfirmation={() =>
          setShowDisableOverrideConfirmationPopup(null)
        }
        confirmationText={
          "Are you sure you want to Disable Override Price for this date? Click yes to confirm, click no to continue without overriding"
        }
        onSuccess={async () => {
          await disablePushOverride(
            showDisableOverrideConfirmationPopup.date,
            showDisableOverrideConfirmationPopup.roomId,
            showDisableOverrideConfirmationPopup.rateId
          );
          setShowDisableOverrideConfirmationPopup(null);
        }}
        open={!!showDisableOverrideConfirmationPopup?.roomId}
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={!!showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(null)}
      >
        {showSnackbar && (
          <SnackbarContent
            sx={{
              backgroundColor: showSnackbar.isError ? "#CA3433" : "#228b22",
              color: "#fff",
            }}
            message={showSnackbar.message}
          />
        )}
      </Snackbar>
    </StyledChannelDialog>
  );
};

const StyledChannelDialog = styled(Dialog)(() => ({
  ".MuiPaper-root": {
    overflow: "visible",
  },
  ".closeButton": {
    position: "absolute",
    right: "10px",
    top: "10px",
    cursor: "pointer",
  },
  ".content": {
    padding: "7px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "visible",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  backgroundColor: theme.palette.common.white,
  ...(selected && {
    backgroundColor: "rgba(25, 118, 210, 0.16)",
    border: "2px solid red",
    "& > td": {
      backgroundColor: "rgba(25, 118, 210, 0.16)",
    },
  }),
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#306fbc",
  color: theme.palette.common.white,
  padding: "4px",
  textAlign: "center",
  borderRight: "1px solid #444",
  [`&.${tableCellClasses.head}`]: {
    fontSize: "12px",
  },
}));

const CustomTableCell = styled(TableCell)(({ theme }) => ({
  padding: "6px",
  textAlign: "center",
  borderBottom: "1px solid #eee",
  borderRight: "1px solid #eee",
  borderTop: "1px solid #eee",
  color: "#555",
  [`&.${tableCellClasses.body}`]: {
    fontSize: "10px",
  },
}));

const RedAsterisk = styled("span")({
  color: "red",
});

const StyledTextField = styled(TextField)`
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* for Firefox */
  input[type="number"] {
    -moz-appearance: textfield;
  }
`;
